import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { useAuth } from "@/auth";
import ITChecklist from "@/components/ITChecklist";
import {
  ShieldCheck, Network, Database, Download, FileText, ClipboardList,
  ArrowRight, Server, Cloud, GitBranch, Lock,
} from "lucide-react";

const DOWNLOADS = [
  { key: "readiness-pdf", label: "Readiness Técnico (PDF)", url: "/readiness/pdf", file: "lala-elite-readiness.pdf" },
  { key: "arch-pdf", label: "Architecture Review (PDF)", url: "/architecture-review/pdf", file: "lala-elite-architecture-review.pdf" },
  { key: "arch-xlsx", label: "Architecture Review (Excel)", url: "/architecture-review/export.xlsx", file: "lala-elite-architecture-review.xlsx" },
  { key: "guia-ti", label: "Guía TI (PDF)", url: "/admin/demo-ti-guide.pdf", file: "lala-elite-guia-ti.pdf" },
  { key: "security", label: "Protocolo de seguridad", url: "/docs/security-protocol", file: "lala-elite-protocolo-seguridad.docx" },
  { key: "revision-pack", label: "Revision Pack · código fuente (ZIP)", url: "/admin/revision-pack.zip", file: "lala-elite-revision-pack.zip" },
];

const DATA_CLOUD = [
  {
    icon: Database, title: "Dónde se guarda la información",
    lines: [
      "Base de datos MongoDB gestionada en la nube, con cifrado en reposo.",
      "Todo el tráfico viaja cifrado por HTTPS/TLS.",
      "Respaldos automáticos del proveedor gestionado; la política de retención se define con TI.",
    ],
  },
  {
    icon: GitBranch, title: "Flujo del dato (negocio)",
    lines: [
      "1. El evaluador captura niveles 1–4 por indicador → se registra como propuesta.",
      "2. El administrador acepta o rechaza cada propuesta (bitácora de auditoría).",
      "3. Solo lo aceptado alimenta el motor de reconocimiento (pesos por categoría).",
      "4. El proveedor ve su resultado en base 10, su nivel y su reporte PDF — solo lectura.",
    ],
  },
  {
    icon: Cloud, title: "Qué se necesita de nube",
    lines: [
      "Nada on-premise: la plataforma corre en contenedores (Kubernetes) administrados.",
      "Lala solo necesita navegador y, si se desea, un subdominio corporativo (CNAME + SSL automático).",
      "Fase 2: Azure AD (SSO + MFA) y credenciales corporativas para integraciones futuras.",
    ],
  },
  {
    icon: Lock, title: "Seguridad aplicada hoy",
    lines: [
      "Acceso por rol (6 perfiles) · el registro de cuentas es exclusivo del administrador.",
      "Bloqueo por fuerza bruta: 5 intentos fallidos → 15 minutos.",
      "Credenciales en variables de entorno · bitácora de auditoría de todas las acciones.",
    ],
  },
];

export default function PortalTI() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user?.role === "owner" || user?.role === "tech_admin") {
      api.get("/readiness/summary").then((r) => setSummary(r.data)).catch(() => {});
    }
  }, [user]);

  const download = async (d) => {
    setBusy(d.key); setErr("");
    try {
      const res = await api.get(d.url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = d.file;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(`No se pudo descargar ${d.label}.`);
    } finally { setBusy(null); }
  };

  const canEdit = user?.role === "tech_admin" || user?.role === "owner";
  const isTech = canEdit; // admin solo ve avance del checklist y la sección de datos

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="portal-ti-page">
      <div className="mb-8">
        <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
          <Server size={18} /> equipo de tecnología
        </div>
        <h1 className="font-display text-4xl sm:text-5xl">Portal TI · LALA Elite</h1>
        <p className="text-[var(--ink-soft)] mt-2 max-w-3xl">
          Todo lo que el equipo de TI necesita revisar y asegurar: arquitectura, seguridad, datos,
          integraciones y el checklist de validación para dejar constancia de su revisión.
        </p>
      </div>

      {err && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="pti-error">{err}</div>}

      {/* Accesos principales */}
      {isTech && (
      <div className="grid md:grid-cols-2 gap-4 mb-10">
        <Link to="/readiness" className="sketch-border bg-white p-5 hover:shadow-md transition-shadow group" data-testid="pti-link-readiness">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-[var(--lala-blue)] font-bold">
                <ShieldCheck size={18} /> Readiness técnico
              </div>
              <p className="text-sm text-[var(--ink-soft)] mt-1">
                Catálogo completo: ciberseguridad, conectividad, arquitectura y DevOps — con estado de cada control.
              </p>
              {summary && (
                <div className="mt-2 text-2xl font-display text-[var(--lala-blue)]" data-testid="pti-readiness-pct">
                  {summary.global_readiness_label} listo
                </div>
              )}
            </div>
            <ArrowRight size={18} className="text-[var(--lala-blue)] group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
        <Link to="/architecture-review" className="sketch-border bg-white p-5 hover:shadow-md transition-shadow group" data-testid="pti-link-architecture">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-[var(--lala-blue)] font-bold">
                <Network size={18} /> Architecture Review
              </div>
              <p className="text-sm text-[var(--ink-soft)] mt-1">
                Los puntos que TI pidió asegurar, con responsable, estado y filtros por dominio.
              </p>
            </div>
            <ArrowRight size={18} className="text-[var(--lala-blue)] group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      </div>
      )}

      {/* Datos y nube */}
      <div className="mb-10">
        <div className="font-hand text-xl text-[var(--lala-blue)]">datos y nube</div>
        <h2 className="font-display text-2xl mb-4">Cómo vive la información</h2>
        <div className="grid md:grid-cols-2 gap-4" data-testid="pti-data-cloud">
          {DATA_CLOUD.map((c) => (
            <div key={c.title} className="sketch-border-soft bg-[var(--cream-soft)] p-5" data-testid={`pti-card-${c.title.slice(0, 10)}`}>
              <div className="flex items-center gap-2 font-bold text-[var(--lala-blue)] mb-2">
                <c.icon size={16} /> {c.title}
              </div>
              <ul className="space-y-1.5">
                {c.lines.map((l, i) => (
                  <li key={i} className="text-sm text-[var(--ink)] flex gap-2">
                    <span className="text-[var(--lala-blue)] shrink-0">·</span>{l}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Descargas */}
      {isTech && (
      <div className="mb-10">
        <div className="font-hand text-xl text-[var(--lala-blue)]">documentación</div>
        <h2 className="font-display text-2xl mb-4">Descargas para TI</h2>
        <div className="flex flex-wrap gap-3" data-testid="pti-downloads">
          {DOWNLOADS.map((d) => (
            <button key={d.key} onClick={() => download(d)} disabled={busy === d.key}
              className="sketch-pill px-4 py-2 text-sm font-semibold border border-[var(--lala-blue)]/40 text-[var(--lala-blue)] bg-white hover:bg-[var(--lala-blue-soft)] inline-flex items-center gap-2 disabled:opacity-50"
              data-testid={`pti-dl-${d.key}`}>
              {busy === d.key ? <FileText size={14} className="animate-pulse" /> : <Download size={14} />}
              {d.label}
            </button>
          ))}
        </div>
      </div>
      )}

      {/* Checklist de validación */}
      <div>
        <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
          <ClipboardList size={16} /> validación
        </div>
        <h2 className="font-display text-2xl mb-1">Checklist de validación TI</h2>
        <p className="text-sm text-[var(--ink-soft)] mb-2">
          {canEdit
            ? "Marca cada punto como Validado, Con observaciones o Pendiente, y deja tu comentario — queda registrado con tu nombre y fecha."
            : "Avance de la revisión del equipo de TI (solo lectura para tu rol)."}
        </p>
        <button onClick={() => download({ key: "checklist-pdf", label: "Constancia del checklist", url: "/it-checklist/export.pdf", file: "lala-elite-checklist-ti.pdf" })}
          disabled={busy === "checklist-pdf"}
          className="mb-4 sketch-pill px-4 py-2 text-sm font-semibold border border-[var(--lala-blue)]/40 text-[var(--lala-blue)] bg-white hover:bg-[var(--lala-blue-soft)] inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="pti-dl-checklist-pdf">
          <Download size={14} /> Descargar constancia (PDF)
        </button>
        <ITChecklist canEdit={canEdit} />
      </div>
    </div>
  );
}
